import { SagaIterator } from '@redux-saga/core';
import { all, takeLatest, put, call } from 'redux-saga/effects';

import { statusMessageSaga } from 'pages/AppManagement/saga';
import { RequestSagaParams } from 'types';

import * as Actions from './slice';
import * as Endpoints from './endpoint';

export function* getCTAList({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getConversionsByMarketer, payload);
    const data =
      response.data._embedded && response.data._embedded.conversions && response.data._embedded.conversions.length > 0
        ? response.data._embedded.conversions
        : [];
    yield put(Actions.fetchConversionListSuccess(data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchConversionListFailure(error));
  }
}

export function* getTagsByMarketer(): SagaIterator {
  try {
    const response = yield call(Endpoints.getTagsByMarketer);
    yield put(Actions.getTagsByMarketerSuccess(response.data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getTagsByMarketerFailure(error));
  }
}

export function* getTagCategoriesByMarketer(): SagaIterator {
  try {
    const response = yield call(Endpoints.getTagCategoriesByMarketer);
    const data =
      response.data._embedded && response.data._embedded.categories && response.data._embedded.categories.length > 0
        ? response.data._embedded.categories
        : [];
    yield put(Actions.getTagCategoriesByMarketerSuccess(data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.getTagCategoriesByMarketerFailure(error));
  }
}

export function* addTagCategory({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.addTagCategory, payload.data);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.getTagCategoriesByMarketer({}));
    yield put(Actions.addTagCategorySuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully Added Category' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.addTagCategoryFailure(error));
  }
}

export function* deleteTag({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.deleteTag, payload.id);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.getTagsByMarketer({}));
    yield put(Actions.deleteTagSuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Tag Deleted.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.deleteTagFailure(error));
  }
}

export function* updateTagCategory({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.editCategory, payload.id, payload.name);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    yield put(Actions.updateTagCategorySuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Successfully updated Category' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateTagCategoryFailure(error));
  }
}

export function* deleteCategory({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.deleteCategory, payload.id);
    if (payload.callBack) {
      yield call(payload.callBack);
    }
    if (payload.tagRefetchRequired) {
      yield put(Actions.getTagsByMarketer({}));
    }
    yield put(Actions.getTagCategoriesByMarketer({}));
    yield put(Actions.deleteCategorySuccess());
    yield call(statusMessageSaga, { type: 'success', message: 'Category Removed.' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.deleteCategoryFailure(error));
  }
}

export function* getContent({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getContent, payload.id);
    const content = response.data;
    if (content?._links?.['content:cta']?.href) {
      try {
        const ctaResponse = yield call(Endpoints.getContentCTAInfo, content?._links?.['content:cta']?.href);
        const ctaData = ctaResponse?.data || { conversion: '' };
        content.ctaData = ctaData;
      } catch (error: any) {
        yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
      }
    }
    yield put(Actions.fetchContentSuccess(content));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchContentFailure(error));
  }
}

export function* getRevisionDetails({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.getRevisionDetails, payload);
    const data =
      response.data._embedded && response.data._embedded.revisions && response.data._embedded.revisions.length > 0
        ? response.data._embedded.revisions
        : [];
    yield put(Actions.fetchRevisionDetailsSuccess(data));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchRevisionDetailsFailure(error));
  }
}

export function* UploadFormManagementWatcherSaga(): SagaIterator {
  yield all([
    yield takeLatest(Actions.fetchConversionList.type, getCTAList),
    yield takeLatest(Actions.getTagsByMarketer.type, getTagsByMarketer),
    yield takeLatest(Actions.getTagCategoriesByMarketer.type, getTagCategoriesByMarketer),
    yield takeLatest(Actions.addTagCategory.type, addTagCategory),
    yield takeLatest(Actions.deleteTag.type, deleteTag),
    yield takeLatest(Actions.updateTagCategory.type, updateTagCategory),
    yield takeLatest(Actions.deleteCategory.type, deleteCategory),
    yield takeLatest(Actions.fetchContent.type, getContent),
    yield takeLatest(Actions.fetchRevisionDetails.type, getRevisionDetails)
  ]);
}

export default UploadFormManagementWatcherSaga;
